import { cos, exp, sin, TAU } from './dmath.ts';
import { L } from './config.ts';

export interface CrowdBody {
  id: number; alive: boolean;
  radius: number; mass: number;
  crowdX: number; crowdZ: number;
  pushX: number; pushZ: number;
}

/** Soft discs on the road. Fixed storage, local pairs, no physics engine or tick allocations. */
export class Crowd {
  private readonly cellSize = 1.2; // larger than the diameter of the heaviest body
  private readonly width = 24;
  private readonly height = 10;
  private readonly originX = L.spawnX - 3;
  private readonly originZ = L.roadZ - 4;
  private readonly heads = new Int16Array(this.width * this.height);
  private next: Int16Array;
  private cells: Int16Array;
  private dx: Float64Array;
  private dz: Float64Array;
  lastChecks = 0;
  lastContacts = 0;

  constructor(capacity: number) {
    this.next = new Int16Array(capacity); this.cells = new Int16Array(capacity);
    this.dx = new Float64Array(capacity); this.dz = new Float64Array(capacity);
  }

  private grow(n: number) {
    const cap = Math.max(n, this.next.length * 2);
    this.next = new Int16Array(cap); this.cells = new Int16Array(cap);
    this.dx = new Float64Array(cap); this.dz = new Float64Array(cap);
  }

  solve(bodies: readonly CrowdBody[], dt: number) {
    this.lastChecks = this.lastContacts = 0;
    const n = bodies.length;
    if (n > this.next.length) this.grow(n);
    for (const b of bodies) b.pushX = b.pushZ = 0;
    if (dt <= 0 || n < 2) return;
    const response = 1 - exp(-dt * 14), maxShift = dt * 1.8;
    // Two gentle relaxation passes converge in a dense group without hard snaps.
    for (let pass = 0; pass < 2; pass++) {
      this.heads.fill(-1); this.dx.fill(0, 0, n); this.dz.fill(0, 0, n);
      for (let i = 0; i < n; i++) {
        const b = bodies[i]; if (!b.alive) continue;
        const x = Math.max(0, Math.min(this.width - 1, Math.floor((b.crowdX - this.originX) / this.cellSize)));
        const z = Math.max(0, Math.min(this.height - 1, Math.floor((b.crowdZ - this.originZ) / this.cellSize)));
        const cell = z * this.width + x;
        this.cells[i] = cell; this.next[i] = this.heads[cell]; this.heads[cell] = i;
      }
      for (let i = 0; i < n; i++) {
        const a = bodies[i]; if (!a.alive) continue;
        const cell = this.cells[i], cx = cell % this.width, cz = Math.floor(cell / this.width);
        for (let z = Math.max(0, cz - 1); z <= Math.min(this.height - 1, cz + 1); z++) {
          for (let x = Math.max(0, cx - 1); x <= Math.min(this.width - 1, cx + 1); x++) {
            for (let j = this.heads[z * this.width + x]; j >= 0; j = this.next[j]) {
              if (j <= i) continue;
              this.lastChecks++;
              const b = bodies[j], r = a.radius + b.radius;
              let vx = b.crowdX - a.crowdX, vz = b.crowdZ - a.crowdZ;
              const dd = vx * vx + vz * vz;
              if (dd >= r * r) continue;
              this.lastContacts++;
              let distance = Math.sqrt(dd);
              if (distance < 1e-5) {
                // Stable direction for coincident spawns; does not consume gameplay RNG.
                let hash = Math.imul(a.id, 73856093) ^ Math.imul(b.id, 19349663);
                hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
                const angle = (hash >>> 0) * (TAU / 4294967296);
                vx = cos(angle); vz = sin(angle); distance = 0;
              } else {
                vx /= distance; vz /= distance;
                // A tiny lateral bias breaks a perfectly straight queue into a natural shuffle.
                if (Math.abs(vz) < 0.035) {
                  vz = (vz !== 0 ? Math.sign(vz) : ((a.id ^ b.id) & 1 ? 1 : -1)) * 0.14;
                  vx *= Math.sqrt(1 - vz * vz);
                }
              }
              const push = Math.min(r - distance, 0.32) * response;
              const shareA = b.mass / (a.mass + b.mass), shareB = 1 - shareA;
              this.dx[i] -= vx * push * shareA; this.dz[i] -= vz * push * shareA;
              this.dx[j] += vx * push * shareB; this.dz[j] += vz * push * shareB;
            }
          }
        }
      }
      for (let i = 0; i < n; i++) {
        const b = bodies[i]; if (!b.alive) continue;
        const x = this.dx[i], z = this.dz[i], length = Math.sqrt(x * x + z * z);
        const k = length > maxShift ? maxShift / length : 1;
        b.crowdX += x * k; b.crowdZ += z * k;
        b.pushX += x * k; b.pushZ += z * k;
      }
    }
  }
}
